import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Send, Inbox, FileText, CheckCircle, AlertTriangle, RefreshCw, Plus, X, Download } from 'lucide-react';
import { theme, cardStyle, inputStyle, labelStyle } from '../theme';

export default function FaxPage({ API_BASE }) {
  const [items, setItems]  = useState([]);
  const [provider, setPro] = useState('stub');
  const [tab, setTab]      = useState('outbound');
  const [showSend, setShowSend] = useState(false);
  const [loading, setL]    = useState(true);

  const load = useCallback(async () => {
    setL(true);
    try {
      const r = await axios.get(`${API_BASE}/fax`, { params: { direction: tab } });
      setItems(r.data.items || []); setPro(r.data.provider);
    } catch (e) { console.error(e); }
    setL(false);
  }, [API_BASE, tab]);

  useEffect(() => { load(); }, [load]);

  return (
    <div style={{ padding: 20 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 18 }}>
        <h2 style={{ margin: 0, color: theme.navy }}>📠 eFax — Digital Fax</h2>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={load} style={btn(theme.secondary)}><RefreshCw size={14}/> Reload</button>
          <button onClick={()=>setShowSend(true)} style={btn(theme.primary)}><Send size={14}/> Send Fax</button>
        </div>
      </div>

      {provider === 'stub' && (
        <div style={{ ...cardStyle, padding: 12, marginBottom: 14, background: '#fef3c7', borderLeft: `4px solid ${theme.warning}` }}>
          <strong style={{ color: '#92400e' }}>⚠️ Stub mode active.</strong> Faxes log only — actual sending requires a paid provider.
          Set <code>FAX_PROVIDER=phaxio</code> + creds in server <code>.env</code> to enable real fax. India me ye optional module hai (mostly insurance pre-auth + lab partner workflows ke liye).
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
        <TabBtn active={tab==='outbound'} onClick={()=>setTab('outbound')}><Send size={13}/> Sent</TabBtn>
        <TabBtn active={tab==='inbound'}  onClick={()=>setTab('inbound')}><Inbox size={13}/> Received</TabBtn>
      </div>

      <div style={{ ...cardStyle, padding: 0, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead style={{ background: theme.primaryLight }}>
            <tr>
              <th style={th}>Date</th>
              <th style={th}>{tab === 'outbound' ? 'To' : 'From'}</th>
              <th style={th}>Subject</th><th style={th}>Pages</th>
              <th style={th}>Status</th><th style={th}>File</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={6} style={{ padding: 30, textAlign: 'center', color: theme.textMuted }}>Loading...</td></tr>}
            {!loading && items.length === 0 && (
              <tr><td colSpan={6} style={{ padding: 30, textAlign: 'center', color: theme.textMuted }}>
                <FileText size={28} style={{ opacity: 0.4 }}/>
                <div style={{ marginTop: 6 }}>No {tab} faxes.</div>
              </td></tr>
            )}
            {items.map(f => (
              <tr key={f._id} style={{ borderTop: `1px solid ${theme.borderLight}` }}>
                <td style={td}>{new Date(f.createdAt).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</td>
                <td style={td}>{tab === 'outbound' ? f.toNumber : f.fromNumber}</td>
                <td style={td}>{f.subject || '—'}</td>
                <td style={td}>{f.pages}</td>
                <td style={td}>
                  <span style={pill(statusColor(f.status))}>
                    {f.status === 'sent' || f.status === 'received' ? <CheckCircle size={11}/> : f.status === 'failed' ? <AlertTriangle size={11}/> : null}
                    {f.status}
                  </span>
                  {f.errorMessage && <div style={{ fontSize: 11, color: theme.danger, marginTop: 2 }}>{f.errorMessage}</div>}
                </td>
                <td style={td}>
                  {(f.filePath || f.fileUrl) && (
                    <a href={f.fileUrl || f.filePath} target="_blank" rel="noreferrer" style={{ color: theme.info, fontSize: 12 }}>
                      <Download size={12}/> Open
                    </a>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showSend && <SendFaxModal API_BASE={API_BASE} onClose={()=>setShowSend(false)} onSent={()=>{ setShowSend(false); load(); }}/>}
    </div>
  );
}

function SendFaxModal({ API_BASE, onClose, onSent }) {
  const [toNumber, setTo]  = useState('');
  const [subject, setSub]  = useState('');
  const [busy, setBusy]    = useState(false);
  const fileRef = useRef(null);

  const send = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file)     return alert('Please select a PDF file');
    if (!toNumber) return alert('To-number required');
    setBusy(true);
    const fd = new FormData();
    fd.append('file', file);
    fd.append('toNumber', toNumber);
    fd.append('subject', subject);
    try {
      await axios.post(`${API_BASE}/fax/send`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      onSent();
    } catch (e) { alert(e.response?.data?.error || e.message); }
    setBusy(false);
  };

  return (
    <div style={overlay}>
      <div style={{ ...cardStyle, padding: 24, width: 'min(480px, 92vw)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <h3 style={{ margin: 0 }}>📠 Send New Fax</h3>
          <button onClick={onClose} style={{ background: 'transparent', border: 'none', cursor: 'pointer' }}><X/></button>
        </div>
        <div style={{ marginTop: 14 }}>
          <label style={labelStyle}>To Number (international format)</label>
          <input value={toNumber} onChange={e=>setTo(e.target.value)} style={inputStyle} placeholder="+91 11 12345678"/>
          <label style={labelStyle}>Subject (optional)</label>
          <input value={subject} onChange={e=>setSub(e.target.value)} style={inputStyle} placeholder="Pre-auth request — Sharma Family"/>
          <label style={labelStyle}>Document (PDF, max 25 MB)</label>
          <input type="file" accept=".pdf,.tif,.tiff" ref={fileRef} style={inputStyle}/>
        </div>
        <button onClick={send} disabled={busy} style={{ ...btn(theme.primary), marginTop: 14, width: '100%' }}>
          <Send size={14}/> {busy ? 'Sending...' : 'Send Fax'}
        </button>
      </div>
    </div>
  );
}

const statusColor = (s) => s === 'sent' || s === 'received' ? theme.success : s === 'failed' ? theme.danger : s === 'sending' ? theme.warning : theme.textMuted;

const TabBtn = ({ active, children, onClick }) => (
  <button onClick={onClick} style={{
    display: 'inline-flex', alignItems: 'center', gap: 6,
    padding: '8px 16px', borderRadius: theme.radiusSm,
    background: active ? theme.primary : '#fff', color: active ? '#fff' : theme.navy,
    border: `1px solid ${active ? theme.primary : theme.border}`, cursor: 'pointer', fontWeight: 600, fontSize: 13,
  }}>{children}</button>
);

const th = { padding: '10px 12px', textAlign: 'left', fontSize: 12, color: theme.navy, fontWeight: 700 };
const td = { padding: '10px 12px', fontSize: 13 };
const btn = (c) => ({ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', background: c, color: '#fff', border: 'none', borderRadius: theme.radiusSm, cursor: 'pointer', fontWeight: 600, fontSize: 13 });
const pill = (c) => ({ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px', background: c + '20', color: c, borderRadius: 12, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4 });
const overlay = { position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 };
